import { Component, OnInit, OnDestroy } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { Router, NavigationEnd, NavigationError } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-version-checker',
    templateUrl: './version-checker.component.html',
    styleUrls: ['./version-checker.component.scss']
})
export class VersionCheckerComponent implements OnInit, OnDestroy {
    isUpdatePending = false;
    private unsubscribe$ = new Subject<void>();

    constructor(
        private swUpdate: SwUpdate,
        private router: Router
    ) {}

    ngOnInit(): void {
        if (!this.swUpdate.isEnabled) {
            console.log('[VersionChecker] Service Worker is not enabled in this mode.');
            return;
        }

        // Perform an immediate check on component initialization
        this.swUpdate.checkForUpdate().catch(() => {});

        // Listen for new version updates ready from Service Worker (Angular 8 SwUpdate API)
        this.swUpdate.available
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((event) => {
                console.warn('[VersionChecker] New application version available:', event);
                this.isUpdatePending = true;
            });

        // Listen for NavigationError to automatically recover from chunk load failures and navigate to target URL
        this.router.events
            .pipe(
                filter((event): event is NavigationError => event instanceof NavigationError),
                takeUntil(this.unsubscribe$)
            )
            .subscribe((event: NavigationError) => {
                const errorStr = (event.error ? (event.error.message || String(event.error)) : '').toLowerCase();
                if (
                    errorStr.includes('chunk') ||
                    errorStr.includes('dynamically imported module') ||
                    errorStr.includes('failed to fetch') ||
                    errorStr.includes('loading css chunk')
                ) {
                    const targetUrl = event.url || window.location.href;
                    console.warn('[VersionChecker] Missing chunk error caught. Navigating to target URL:', targetUrl);
                    if (this.swUpdate.isEnabled) {
                        this.swUpdate.activateUpdate().then(() => {
                            window.location.href = targetUrl;
                        }).catch(() => {
                            window.location.href = targetUrl;
                        });
                    } else {
                        window.location.href = targetUrl;
                    }
                }
            });

        // Trigger checkForUpdate() on route changes
        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                takeUntil(this.unsubscribe$)
            )
            .subscribe(() => {
                if (!this.isUpdatePending) {
                    this.swUpdate.checkForUpdate().catch(() => {});
                }
            });
    }

    reloadPage(): void {
        console.log('[VersionChecker] User clicked Reload Now. Activating new version...');
        this.isUpdatePending = false;
        this.swUpdate.activateUpdate().then(() => {
            window.location.reload();
        }).catch(() => {
            window.location.reload();
        });
    }

    dismissToast(): void {
        this.isUpdatePending = false;
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
